import { useState, useRef, useEffect, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { trpc } from '@org/clients/school';
import { keepPreviousData } from '@tanstack/react-query';
import { Selo, seloCharacteristic } from '../../components/selo';
import { HEADLINE_MAX_LINES, headlineFontSize } from '../../lib/poster-headline';
import { Portrait } from '../../components/portrait';
import { PortraitPlate } from '../../components/portrait-plate';
import { AutoMeetCountdown } from '../../components/auto-meet-countdown';
import { ArrivalBanner, ARRIVAL_SLIDE_MS } from '../../components/arrival-banner';
import { FeatureFlagGate } from '../../components/feature-flag-gate';
import { isCeremonyWallSettled, resolveCeremonyUnit } from '../../lib/ceremony';
import { canOpenStory, isStoryFlowBlocked } from '../../lib/story';
import { findWallFocusUnit, resolveArrivalUnit } from '../../lib/arrival';
import {
  StoryRing,
  STORY_RING_EXTRA,
  STORY_RING_STYLES,
} from '../../components/story/story-ring';
import { useStoryFlow } from '../../components/story/use-story-flow';

// -- Responsive hook (replaces Tailwind responsive classes) --
const useWidth = () => {
  const [w, setW] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 640,
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

export const Route = createFileRoute('/_app/journey')({
  component: GatedJourneyPage,
  validateSearch: (search: Record<string, unknown>) => ({
    celebrate: typeof search.celebrate === 'string' ? search.celebrate : undefined,
    journeyId: typeof search.journeyId === 'string' ? search.journeyId : undefined,
    // Set by a class back cover when the student finishes a unit's last class:
    // the id of the unit whose selo ceremony should open on arrival.
    ceremony: typeof search.ceremony === 'string' ? search.ceremony : undefined,
  }),
});

function GatedJourneyPage() {
  return (
    <FeatureFlagGate flag="journey">
      <JourneyPage />
    </FeatureFlagGate>
  );
}

// -- Feminist color palette (from journey-centric-wall prototype) --
const P = {
  maxRed: '#D41920',
  sizzle: '#E84B5A',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  black: '#1A1018',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  offWhite: '#FEF3EF',
  blush: '#FDF0ED',
  roseMist: '#F9E4E0',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightBorder: '#F0DAD5',
  shadow: 'rgba(180, 80, 80, 0.12)',
  shadowDeep: 'rgba(120, 40, 40, 0.18)',
};

const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

// -- Rotation cycle for poster tilt --
const ROTATIONS = [-1.8, 1.2, -0.6, 1.6];

// -- Ceremony geometry --
// Height the caption plate takes at the bottom of the viewport; the ceremony
// poster and the portrait medallion are centred in the band above it.
const CEREMONY_CAPTION_BAND = 230;
// Diameter of the portrait medallion the selo is stamped onto.
const CEREMONY_MEDALLION = 196;

// Nearest ancestor that actually scrolls (the app shell scrolls a content
// pane, not the window), or null to fall back to window scrolling.
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// -- Reduced-motion preference (checked at render/effect time so JS-driven
//    effects — confetti, typewriter — respect it, not just CSS keyframes) --
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Gap kept above a unit section when the wall scrolls to it.
const SECTION_SCROLL_GAP = 14;

// Bring a unit section to the top of the scrolling pane (the app shell
// scrolls a content pane, not the window). `onlyIfBelow` skips the scroll
// when the section already sits in the upper part of the view, so opening
// the wall on its first unit does not jump for nothing.
function scrollSectionToTop(
  el: HTMLElement,
  opts: { reduced: boolean; onlyIfBelow?: boolean },
) {
  const scroller = findScrollableAncestor(el);
  const rect = el.getBoundingClientRect();
  const viewTop = scroller ? scroller.getBoundingClientRect().top : 0;
  const viewHeight = scroller ? scroller.clientHeight : window.innerHeight;
  const offset = rect.top - viewTop;
  if (opts.onlyIfBelow && offset >= 0 && offset < viewHeight * 0.55) return;
  const scrollOpts: ScrollToOptions = {
    top: offset - SECTION_SCROLL_GAP,
    behavior: opts.reduced ? 'auto' : 'smooth',
  };
  if (scroller) scroller.scrollBy(scrollOpts);
  else window.scrollBy(scrollOpts);
}

/**
 * Units whose arrival banner already showed since the app was loaded.
 * Module-level on purpose: leaving the wall and coming back in the same
 * session must not bring the banner up again while the server round trip
 * (markArrivalSeen) is still on its way.
 */
const sessionDismissedArrivals = new Set<string>();

// -- Types --
type WallData = NonNullable<
  ReturnType<typeof trpc.progress.getStudentJourneyWall.useQuery>['data']
>;
type WallUnit = WallData['units'][number];

// -- CSS Keyframes (injected once via <style>) --
const CEREMONY_STYLES = `
@keyframes confettiBurst {
  0% { opacity: 1; transform: translate(0, 0) rotate(0deg) scale(1); }
  80% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--dx, 80px), var(--dy, -120px)) rotate(var(--rot, 720deg)) scale(0.2); }
}
@keyframes shineSweep {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
}
@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes breathe {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}
@keyframes slideReveal {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes glowPulse {
  0%, 100% { box-shadow: 6px 8px 24px rgba(120,40,40,0.18); }
  50% { box-shadow: 6px 8px 36px rgba(212,25,32,0.22), 6px 8px 24px rgba(120,40,40,0.18); }
}
@keyframes currentRing {
  0%, 100% { box-shadow: 0 0 0 2px transparent; }
  50% { box-shadow: 0 0 0 4px currentColor; }
}
.class-tile-current-ring {
  animation: currentRing 1.8s ease infinite;
}
@keyframes seloStamp {
  0% { transform: scale(2.4) rotate(24deg); opacity: 0; }
  100% { transform: scale(1) rotate(12deg); opacity: 1; }
}
@keyframes seloRing {
  0% { opacity: .8; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.9); }
}
@keyframes ceremonyFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes medallionReveal {
  0% { opacity: 0; transform: scale(.78) translateY(18px); }
  70% { opacity: 1; transform: scale(1.03) translateY(0); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
.ceremony-medallion {
  animation: medallionReveal .62s cubic-bezier(.22,1,.36,1) .18s both;
}
.selo-stamp {
  animation: seloStamp .45s cubic-bezier(.34,1.56,.64,1) both;
}
.selo-stamp-ring {
  animation: seloRing .7s ease .1s both;
}
@media (prefers-reduced-motion: reduce) {
  .class-tile-current-ring { animation: none; }
  .poster-pulse, .cta-pulse { animation: none; }
  .selo-stamp, .selo-stamp-ring, .ceremony-medallion { animation: none; }
}

.poster-hover:hover {
  transform: rotate(0deg) scale(1.02) !important;
  box-shadow: 8px 12px 32px rgba(120,40,40,0.25) !important;
}
.poster-pulse {
  animation: glowPulse 3s ease infinite;
}
.cta-pulse {
  animation: breathe 2.5s ease infinite;
}

/* Tactile button — Sentry/Indeed style: solid color with a bottom-edge ridge that
   compresses when pressed. Vertical-only motion makes the press feel physical.
   Colors flow through via --tactile-shadow / --tactile-shadow-hover / --tactile-shadow-press. */
.tactile-btn {
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
  box-shadow: var(--tactile-shadow);
  will-change: transform, box-shadow;
}
.tactile-btn:hover {
  transform: translateY(-1px);
  box-shadow: var(--tactile-shadow-hover);
  filter: brightness(1.05);
}
.tactile-btn:active {
  transform: translateY(3px);
  box-shadow: var(--tactile-shadow-press);
  filter: brightness(0.97);
}

/* Tactile row — elevated card-button. Stack of these reads as a list of pressable
   buttons (Indeed/Sentry signature: tinted face + bottom ridge that disappears on press). */
.tactile-row {
  cursor: pointer;
  position: relative;
  background: var(--tactile-row-bg);
  border: 1px solid var(--tactile-row-border);
  box-shadow: var(--tactile-row-shadow);
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease, filter 0.12s ease;
  will-change: transform, box-shadow;
}
.tactile-row:hover {
  background: var(--tactile-row-hover-bg);
  transform: translateY(-1px);
  box-shadow: var(--tactile-row-shadow-hover);
  filter: brightness(1.03);
}
.tactile-row:active {
  transform: translateY(2px);
  box-shadow: var(--tactile-row-shadow-press);
}

@media (prefers-reduced-motion: reduce) {
  .tactile-btn, .tactile-row {
    transition: box-shadow 0.12s ease, background 0.12s ease, filter 0.12s ease;
  }
  .tactile-btn:hover, .tactile-btn:active,
  .tactile-row:hover, .tactile-row:active { transform: none; }
}

/* Story ring around a poster portrait (opens her story) */
${STORY_RING_STYLES}
.story-ring-btn:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 3px; }
/* "Read her story" link under her name (same action as the ring) */
.story-read-link { transition: transform 0.15s ease, opacity 0.15s ease; }
.story-read-link:hover, .story-read-link:focus-visible { opacity: 1 !important; transform: translateX(2px); }
.story-read-link:focus-visible { outline: 2px solid ${P.charm}; outline-offset: 2px; }

/* Journey banner dropdown */
.journey-menu-item:hover { background: ${P.blush}; }
.journey-menu-item:focus-visible { outline: 2px solid ${P.charm}; outline-offset: -2px; }
.journey-pill-btn:hover { box-shadow: 0 4px 18px ${P.shadowDeep}; }
.journey-pill-btn:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }

::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: ${P.cream}; }
::-webkit-scrollbar-thumb { background: ${P.charm}88; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: ${P.charm}; }
`;

// -- Brand poster styles: predetermined color combos cycling per unit index --
// Based on E2E brand identity: Maximum Red, Sizzling Red, Charm Pink, Baby Pink, Black
const POSTER_STYLES: Array<{ bg: string; text: string; tape: string }> = [
  { bg: P.maxRed, text: P.white, tape: 'rgba(255,255,255,0.15)' },
  { bg: P.baby, text: P.maxRed, tape: 'rgba(212,25,32,0.1)' },
  { bg: P.black, text: P.white, tape: 'rgba(255,255,255,0.15)' },
  { bg: P.charm, text: P.darkText, tape: 'rgba(212,25,32,0.1)' },
  { bg: P.sizzle, text: P.white, tape: 'rgba(255,255,255,0.15)' },
  { bg: P.cream, text: P.maxRed, tape: 'rgba(212,25,32,0.1)' },
];

function getPosterStyle(index: number) {
  return POSTER_STYLES[index % POSTER_STYLES.length]!;
}

// -- Tape Strip --
function Tape({
  width = 36,
  left,
  right,
  top = -5,
  rotation = -12,
  color = 'rgba(255,255,255,0.15)',
}: {
  width?: number;
  left?: number;
  right?: number;
  top?: number;
  rotation?: number;
  color?: string;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        ...(left !== undefined ? { left } : {}),
        ...(right !== undefined ? { right } : {}),
        width,
        height: 11,
        background: color,
        transform: `rotate(${rotation}deg)`,
        zIndex: 5,
        backdropFilter: 'blur(1px)',
      }}
    />
  );
}

// -- PasteDrip Effect (wheat paste) --
function PasteDrip({ color }: { color: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: -8,
        left: 0,
        right: 0,
        height: 12,
        background: `linear-gradient(180deg, ${color}, transparent)`,
        opacity: 0.2,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
}


// -- Journey Banner: the level pill IS the journey switcher. With one journey
//    it renders as a static pill; with 2+ it opens a dropdown of current and
//    past journeys (replaces the old stand-alone <select>). --
function JourneyBanner({
  level,
  name,
  currentJourneyId,
  onJourneyChange,
  isMobile,
}: {
  level: string;
  name: string;
  currentJourneyId: string;
  onJourneyChange: (id: string | undefined) => void;
  isMobile: boolean;
}) {
  const { t } = useTranslation('p2e');
  const { data: journeys } = trpc.progress.getStudentJourneys.useQuery({});
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const switchable = (journeys?.length ?? 0) >= 2;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const assigned = (journeys ?? []).filter((j) => j.source === 'assignment');
  const historical = (journeys ?? []).filter((j) => j.source === 'historical');

  const groupHeader = (label: string) => (
    <div
      style={{
        fontFamily: oswald,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.18em',
        color: P.warmGray,
        padding: '12px 16px 6px',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
  );

  const renderItem = (
    j: {
      journeyId: string;
      name: string | null;
      level: string | null;
      // Purchased/granted access whose window has closed (server-computed).
      expired?: boolean;
    },
    past: boolean,
  ) => {
    const selected = j.journeyId === currentJourneyId;
    const choose = () => {
      onJourneyChange(j.journeyId);
      setOpen(false);
    };
    return (
      <div
        key={j.journeyId}
        role="option"
        aria-selected={selected}
        tabIndex={0}
        className="journey-menu-item"
        onClick={choose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            choose();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 16px',
          minHeight: 44,
          cursor: 'pointer',
          fontFamily: oswald,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: P.darkText,
        }}
      >
        {j.level && (
          <span
            style={{
              fontSize: 11,
              color: P.white,
              background: past ? P.warmGray : P.maxRed,
              padding: '2px 7px',
              letterSpacing: '0.08em',
              flexShrink: 0,
            }}
          >
            {j.level}
          </span>
        )}
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {j.name}
          {j.expired ? ` ${t('journey.expiredSuffix')}` : ''}
        </span>
        {selected && (
          <span aria-hidden style={{ marginLeft: 'auto', color: P.maxRed }}>
            {'✓'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        display: 'inline-block',
        marginTop: isMobile ? 22 : 28,
      }}
    >
      <h1
        data-testid="journey-heading"
        className={switchable ? 'journey-pill-btn' : undefined}
        role={switchable ? 'button' : undefined}
        tabIndex={switchable ? 0 : undefined}
        aria-expanded={switchable ? open : undefined}
        aria-haspopup={switchable ? 'listbox' : undefined}
        onClick={switchable ? () => setOpen((o) => !o) : undefined}
        onKeyDown={
          switchable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen((o) => !o);
                }
              }
            : undefined
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 12,
          margin: 0,
          padding: '10px 20px',
          background: P.white,
          border: `1px solid ${P.lightBorder}`,
          boxShadow: `0 2px 12px ${P.shadow}`,
          cursor: switchable ? 'pointer' : 'default',
          userSelect: 'none',
          transition: 'box-shadow 0.15s ease',
        }}
      >
        <span
          style={{
            fontFamily: oswald,
            fontSize: 24,
            fontWeight: 700,
            color: P.maxRed,
          }}
        >
          {level}
        </span>
        <div
          style={{
            width: 1,
            height: 24,
            background: P.lightBorder,
          }}
        />
        <span
          style={{
            fontFamily: oswald,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: P.darkText,
          }}
        >
          {name}
        </span>
        {switchable && (
          <span
            aria-hidden
            style={{
              fontSize: 11,
              color: P.warmGray,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
              marginLeft: 2,
            }}
          >
            {'▾'}
          </span>
        )}
      </h1>

      {switchable && open && (
        <div
          role="listbox"
          aria-label={t('journey.selectJourney')}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            minWidth: 280,
            maxWidth: 'min(92vw, 360px)',
            background: P.white,
            border: `1px solid ${P.lightBorder}`,
            boxShadow: `0 14px 40px ${P.shadowDeep}`,
            zIndex: 60,
            textAlign: 'left',
            paddingBottom: 6,
          }}
        >
          {assigned.length > 0 && (
            <>
              {groupHeader(t('journey.currentJourneys'))}
              {assigned.map((j) => renderItem(j, false))}
            </>
          )}
          {historical.length > 0 && (
            <>
              {groupHeader(t('journey.pastJourneys'))}
              {historical.map((j) => renderItem(j, true))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// -- ExpandablePoster: single component, classes expand INSIDE --
function ExpandablePoster({
  unit,
  index,
  isExpanded,
  onToggle,
  onCeremony,
  isMobile,
  journeyId,
  isHistorical,
  isNextLocked,
  ceremonyActive,
  onOpenStory,
  onAutoMeet,
}: {
  unit: WallUnit;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onCeremony: (unit: WallUnit) => void;
  isMobile: boolean;
  journeyId: string;
  isHistorical: boolean;
  isNextLocked: boolean;
  ceremonyActive: boolean;
  onOpenStory?: (unitId: string) => void;
  /** set when her story should open by itself (5 s countdown on her plate) */
  onAutoMeet?: () => void;
}) {
  const { t } = useTranslation('p2e');
  const navigate = useNavigate();
  const isCompleted = unit.status === 'completed';
  const isCurrent = unit.status === 'in_progress';
  const active = isCompleted || isCurrent;
  const isLocked = unit.locked;
  const rotation = ROTATIONS[index % ROTATIONS.length]!;
  const posterStyle = getPosterStyle(index);
  const bgColor = posterStyle.bg;
  const textColor = posterStyle.text;
  const tapeColor = posterStyle.tape;
  const accent = bgColor === P.baby ? P.maxRed : bgColor;
  // Small-text legibility: Maximum Red on the pink/cream posters can't reach
  // WCAG 4.5:1 at any opacity, so small metadata switches to the dark ink
  // there. Large display text (headline, name) keeps the brand red (3:1 ok).
  const metaColor = textColor === P.maxRed ? P.darkText : textColor;
  // The current poster (collapsed) floats its "start this unit" button over a
  // cream fade. The woman attribution sits above that fade, in dark ink, so
  // her portrait stays visible and tappable there.
  const showUnitCta = isCurrent && !isCompleted && !isExpanded;
  const attributionInk = showUnitCta ? P.darkText : textColor;
  const attributionMeta = showUnitCta ? P.darkText : metaColor;
  const attributionGap = showUnitCta ? P.cream : bgColor;
  const womanInitial =
    unit.woman?.name?.charAt(0) ?? unit.name?.charAt(0) ?? 'E';
  // Open poster: her collage cut-out big, pasted over the poster's edge
  // (right edge on wide screens, top edge on a phone). The small portrait in
  // the attribution row steps aside; the plate opens her story instead.
  const bigPortrait = isExpanded && !!unit.woman?.portraitUrl;
  const plateBrush =
    bgColor === P.maxRed || bgColor === P.sizzle
      ? P.charm
      : bgColor === P.black
        ? P.sizzle
        : P.sizzle;
  const classesDone = unit.classesCompleted ?? 0;
  const classesTotal = unit.classesTotal ?? 0;
  // Per-class EP split (client-side): the remainder lands on the last class —
  // a finisher's bonus.
  const totalEP = unit.empowermentPoints ?? 0;
  const n = unit.classes.length;
  const baseEP = n > 0 ? Math.floor(totalEP / n) : 0;
  const rowEPFor = (i: number) =>
    baseEP + (i === n - 1 ? totalEP - baseEP * n : 0);
  const showEP = totalEP > 0 && n > 0;
  const earnedEP = unit.classes.reduce(
    (sum, c, i) => sum + (c.done ? rowEPFor(i) : 0),
    0,
  );
  // Poster headline: use quote (woman's quote), fall back to unit.name
  const headline = unit.quote ?? unit.name ?? `Unit ${unit.order + 1}`;

  // Next actionable class in this unit (first not-done, else first) — target
  // for both Continue CTAs so they always DO something.
  const nextClass = unit.classes.find((c) => !c.done) ?? unit.classes[0];
  const goToNextClass = () => {
    if (!nextClass) return;
    navigate({
      to: '/classes/$classId/block/$blockIdx',
      params: { classId: nextClass.classId, blockIdx: '0' },
    });
  };

  if (isLocked) {
    // Curiosity-gap teaser: only the FIRST locked unit shows who's coming —
    // field and era visible, name redacted. Deeper units stay generic so the
    // information gap stays scarce (teasing everything would close it).
    if (isNextLocked && unit.woman?.field) {
      return (
        <div
          style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : 340,
            background: P.roseMist,
            position: 'relative',
            transform: `rotate(${rotation * 0.5}deg)`,
            boxShadow: `5px 7px 20px ${P.shadow}`,
            padding: '26px 22px 24px',
            overflow: 'hidden',
          }}
        >
          {/* Torn corner hinting at the poster color underneath */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 52,
              height: 52,
              background: bgColor,
              clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
              opacity: 0.85,
            }}
          />
          <div
            style={{
              fontFamily: oswald,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.24em',
              color: P.warmGray,
              marginBottom: 14,
            }}
          >
            UNIT {unit.order + 1}
          </div>
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              background: '#EAD3CE',
              border: '2.5px solid #D9BCB6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: oswald,
              fontSize: 26,
              fontWeight: 700,
              color: '#B99C96',
              marginBottom: 12,
            }}
          >
            ?
          </div>
          {/* Redacted name */}
          <div aria-hidden style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <div style={{ width: 62, height: 13, background: '#DEC5BF', borderRadius: 2 }} />
            <div style={{ width: 88, height: 13, background: '#DEC5BF', borderRadius: 2 }} />
          </div>
          <div
            style={{
              fontFamily: oswald,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: P.darkText,
              opacity: 0.8,
              textTransform: 'uppercase',
            }}
          >
            {unit.woman.field}
          </div>
          {unit.woman.years && (
            <div
              style={{
                fontFamily: dmSerif,
                fontSize: 11,
                fontStyle: 'italic',
                color: P.warmGray,
                marginTop: 2,
              }}
            >
              {unit.woman.years}
            </div>
          )}
          <div
            style={{
              fontFamily: dmSerif,
              fontSize: 13,
              fontStyle: 'italic',
              color: P.midText,
              margin: '14px 0 0',
              lineHeight: 1.5,
            }}
          >
            {t('journey.teaserRevealHint')}
          </div>
          <div
            style={{
              marginTop: 14,
              display: 'inline-block',
              fontFamily: oswald,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: P.maxRed,
              border: `1px solid ${P.maxRed}33`,
              background: `${P.maxRed}08`,
              padding: '5px 10px',
            }}
          >
            {t('journey.teaserUnlocksWith', { unit: unit.order })}
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : 340,
          padding: '44px 24px',
          border: `2px dashed ${P.lightBorder}`,
          background: `${P.roseMist}55`,
          position: 'relative',
          transform: `rotate(${rotation * 0.5}deg)`,
          opacity: 0.45,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 28, opacity: 0.3, marginBottom: 8 }}>
          &#x1F512;
        </div>
        <div
          style={{
            fontFamily: oswald,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.2em',
            color: P.warmGray,
          }}
        >
          {t('journey.comingSoon')}
        </div>
        <div
          style={{
            fontFamily: dmSerif,
            fontSize: 11,
            fontStyle: 'italic',
            color: P.warmGray,
            opacity: 0.6,
            marginTop: 6,
          }}
        >
          {t('journey.keepBuilding')}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onToggle}
      className={
        isExpanded ? '' : isCurrent ? 'poster-pulse' : 'poster-hover'
      }
      style={{
        width: '100%',
        maxWidth: isExpanded || isMobile ? '100%' : 340,
        background: bgColor,
        position: 'relative',
        transform: isExpanded ? 'rotate(0deg)' : `rotate(${rotation}deg)`,
        cursor: 'pointer',
        boxShadow: isExpanded
          ? `0 16px 48px ${P.shadowDeep}, 0 0 0 1px ${bgColor}22`
          : `6px 8px 24px ${P.shadowDeep}, 0 2px 0 ${bgColor}44`,
        // Open: her plate overhangs the poster's edge, so nothing may clip.
        overflow: isExpanded ? 'visible' : 'hidden',
        transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {bigPortrait && unit.woman && (
        <div
          data-testid="poster-portrait-plate"
          style={{
            position: 'absolute',
            zIndex: 4,
            animation: 'fadeSlideIn 0.45s ease 0.15s both',
            ...(isMobile
              ? { top: -20, left: '50%', transform: 'translateX(-50%)' }
              : { top: -34, right: -52 }),
          }}
        >
          <PortraitPlate
            url={unit.woman.portraitUrl}
            name={unit.woman.name}
            initial={womanInitial}
            size={isMobile ? 180 : 250}
            brushColor={plateBrush}
            onOpen={
              onOpenStory && canOpenStory(unit)
                ? () => onOpenStory(unit.unitId)
                : undefined
            }
            openLabel={t(
              unit.story?.seenAt ? 'story.portrait.open' : 'story.portrait.openNew',
              { name: unit.woman.name },
            )}
            storySeen={!!unit.story?.seenAt}
          />
          {onAutoMeet && <AutoMeetCountdown onFire={onAutoMeet} color={P.maxRed} />}
        </div>
      )}
      {/* Tape strips -- fade out when expanded */}
      <div
        style={{
          opacity: isExpanded ? 0 : 1,
          transition: 'opacity 0.3s ease',
        }}
      >
        <Tape
          width={38}
          left={12}
          top={-6}
          rotation={-10}
          color={tapeColor}
        />
        <Tape
          width={28}
          right={16}
          top={-4}
          rotation={6}
          color={tapeColor}
        />
      </div>
      {!isExpanded && <PasteDrip color={bgColor} />}

      {/* Ceremony stamp: the selo slams onto the poster under the blackout */}
      {ceremonyActive && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 84,
            height: 84,
            zIndex: 6,
            pointerEvents: 'none',
          }}
        >
          <div
            aria-hidden
            className="selo-stamp-ring"
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `2px solid ${P.white}`,
              opacity: 0,
            }}
          />
          <div
            className="selo-stamp"
            style={{ transform: 'rotate(12deg)' }}
          >
            <Selo
              symbol={unit.woman?.symbol}
              label={seloCharacteristic(unit.badge, unit.name)}
              background={accent}
              size={84}
              rotate={0}
            />
          </div>
        </div>
      )}

      {/* Current poster: fade overlay (only when collapsed) */}
      {showUnitCta && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(180deg, transparent 35%, ${P.cream}cc 70%, ${P.cream})`,
            zIndex: 3,
            pointerEvents: 'none',
            transition: 'opacity 0.4s ease',
          }}
        />
      )}

      {/* Poster Header (always visible) */}
      <div
        style={{
          padding: isExpanded
            ? bigPortrait
              ? isMobile
                ? '186px 18px 0'
                : '32px 236px 0 28px'
              : isMobile
                ? '24px 18px 0'
                : '32px 28px 0'
            : showUnitCta
              ? isMobile
                ? '22px 18px 76px'
                : '28px 24px 80px'
              : isMobile
                ? '22px 18px 18px'
                : '28px 24px 22px',
          position: 'relative',
          // Room for the plate on the right before the class list starts.
          minHeight: bigPortrait && !isMobile ? 250 : undefined,
          // On the current poster the header has no stacking context of its
          // own, so the attribution row below can sit above the cream fade
          // (z 3) while the quote stays underneath it.
          zIndex: showUnitCta ? 'auto' : 2,
          transition: 'padding 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Unit label */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontFamily: oswald,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.18em',
              color: metaColor,
              opacity: 0.9,
            }}
          >
            UNIT {unit.order + 1}
          </div>
          {isCompleted && !isExpanded && (
            <Selo
              symbol={unit.woman?.symbol}
              label={seloCharacteristic(unit.badge, unit.name)}
              background={accent}
              size={44}
              style={{ marginTop: -4 }}
            />
          )}
          {isExpanded && (
            <div
              className="tactile-btn"
              style={{
                fontFamily: oswald,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.18em',
                color: textColor,
                background: `${textColor}18`,
                padding: '6px 14px',
                border: `1px solid ${textColor}40`,
                animation: 'fadeSlideIn 0.4s ease 0.2s both',
                ['--tactile-shadow' as string]: `0 4px 0 ${textColor}38`,
                ['--tactile-shadow-hover' as string]: `0 5px 0 ${textColor}55`,
                ['--tactile-shadow-press' as string]: `0 1px 0 ${textColor}25`,
              } as React.CSSProperties}
            >
              &#x2715; {t('journey.close')}
            </div>
          )}
        </div>

        {/* Quote -- shrinks when expanded, and sets smaller / clamps for long
            quotes so the poster keeps its footprint */}
        <div
          style={{
            fontFamily: oswald,
            // Next to her big plate the quote is the poster's voice: a third
            // larger than the plain open poster.
            fontSize: Math.round(
              headlineFontSize(headline, isMobile, isExpanded) * (bigPortrait ? 1.3 : 1),
            ),
            fontWeight: 700,
            lineHeight: 1.05,
            letterSpacing: '0.01em',
            color: textColor,
            opacity: isCurrent && !isCompleted && !isExpanded ? 0.65 : 1,
            marginBottom: isExpanded ? 8 : 20,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: isExpanded
              ? HEADLINE_MAX_LINES.expanded
              : HEADLINE_MAX_LINES.collapsed,
            overflow: 'hidden',
            transition:
              'font-size 0.5s cubic-bezier(0.22, 1, 0.36, 1), margin 0.5s ease, opacity 0.4s ease',
          }}
        >
          {headline}
        </div>

        {/* Woman attribution */}
        {unit.woman && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 0,
              position: 'relative',
              zIndex: showUnitCta ? 4 : undefined,
              transition: 'margin 0.4s ease',
            }}
          >
            {!bigPortrait && (onOpenStory && canOpenStory(unit) ? (
              // Her story is published: the portrait is the way back into it.
              // The ring takes STORY_RING_EXTRA px, so the portrait shrinks by
              // as much and the poster layout stays put.
              <StoryRing
                seen={!!unit.story?.seenAt}
                label={t(
                  unit.story?.seenAt
                    ? 'story.portrait.open'
                    : 'story.portrait.openNew',
                  { name: unit.woman.name },
                )}
                gapColor={attributionGap}
                seenColor={`${attributionInk}73`}
                compact={isExpanded}
                onOpen={() => onOpenStory(unit.unitId)}
              >
                <Portrait
                  url={unit.woman.portraitUrl}
                  initial={womanInitial}
                  name={unit.woman.name}
                  color={attributionInk}
                  size={(isExpanded ? 48 : 80) - STORY_RING_EXTRA}
                />
              </StoryRing>
            ) : (
              <Portrait
                url={unit.woman.portraitUrl}
                initial={womanInitial}
                name={unit.woman.name}
                color={attributionInk}
                size={isExpanded ? 48 : 80}
              />
            ))}
            <div>
              <div
                style={{
                  fontFamily: oswald,
                  fontSize: isExpanded ? (bigPortrait ? 13 : 10) : 12,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: attributionInk,
                  opacity: 0.8,
                  transition: 'font-size 0.4s ease',
                }}
              >
                &mdash; {unit.woman.name}
              </div>
              {unit.woman.years && (
                <div
                  style={{
                    fontFamily: dmSerif,
                    fontSize: bigPortrait ? 13 : 11,
                    fontStyle: 'italic',
                    color: attributionMeta,
                    opacity: 0.75,
                    marginTop: 1,
                  }}
                >
                  {unit.woman.years}
                </div>
              )}
              {onOpenStory && canOpenStory(unit) && (
                // Spelled-out way in: the ring alone did not read as a button.
                <button
                  type="button"
                  className="story-read-link"
                  data-testid="story-read-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenStory(unit.unitId);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    margin: bigPortrait ? '8px 0 0' : '4px 0 0',
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                    fontFamily: oswald,
                    fontSize: bigPortrait ? 12.5 : 10,
                    fontWeight: 700,
                    letterSpacing: '0.16em',
                    color: attributionMeta,
                    opacity: 0.9,
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                    textDecorationThickness: 1.5,
                  }}
                >
                  {!unit.story?.seenAt && (
                    <span
                      style={{
                        background: P.maxRed,
                        color: P.white,
                        padding: '1px 5px',
                        fontSize: 8,
                        letterSpacing: '0.14em',
                        textDecoration: 'none',
                        lineHeight: 1.4,
                      }}
                    >
                      {t('story.portrait.new')}
                    </span>
                  )}
                  {t('story.portrait.read')} <span aria-hidden>&rarr;</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Current poster: CTA (only when collapsed).
          Uses the platform's tactile-btn vocabulary (matches Continue/Claim
          buttons elsewhere). The parent poster still glow-pulses to signal
          "this is the active unit," so the CTA itself stays static. */}
      {showUnitCta && (
        <div
          style={{
            position: 'absolute',
            bottom: 18,
            left: 0,
            right: 0,
            textAlign: 'center',
            zIndex: 5,
          }}
        >
          <button
            className="tactile-btn"
            onClick={(e) => {
              e.stopPropagation();
              goToNextClass();
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              background: P.maxRed,
              padding: '11px 22px',
              fontFamily: oswald,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: P.white,
              border: 'none',
              ['--tactile-shadow' as string]: '0 5px 0 rgba(120, 8, 14, 0.95), 0 8px 16px rgba(0,0,0,0.18)',
              ['--tactile-shadow-hover' as string]: '0 6px 0 rgba(120, 8, 14, 1), 0 10px 20px rgba(0,0,0,0.22)',
              ['--tactile-shadow-press' as string]: '0 1px 0 rgba(120, 8, 14, 0.95), 0 2px 6px rgba(0,0,0,0.14)',
            } as React.CSSProperties}
          >
            {t(classesDone > 0 ? 'journey.continueThisUnit' : 'journey.startThisUnit')}
            <span
              aria-hidden
              style={{ fontSize: 14, lineHeight: 1, opacity: 0.85 }}
            >
              &rarr;
            </span>
          </button>
        </div>
      )}

      {/* Expanded: Classes Section (inside poster) */}
      <div
        style={{
          maxHeight: isExpanded ? 500 : 0,
          opacity: isExpanded ? 1 : 0,
          overflow: 'hidden',
          transition:
            'max-height 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease 0.15s',
        }}
      >
        <div style={{ padding: isMobile ? '0 18px 22px' : '0 28px 28px' }}>
          {/* Divider */}
          <div
            style={{
              height: 1,
              background: `${textColor}20`,
              margin: '18px 0',
            }}
          />

          {/* Classes header + EP subtotal */}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 10,
              fontFamily: oswald,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: metaColor,
              opacity: 0.75,
              marginBottom: 14,
            }}
          >
            <span>
              {t('journey.classes')} &middot; {classesDone}/{classesTotal}
            </span>
            {showEP && (
              <span>{t('journey.epInUnit', { points: earnedEP })}</span>
            )}
          </div>

          {/* Class items — tri-state: completed / current / future (D4: all tiles remain clickable) */}
          {(() => {
            const currentClassIdx = unit.classes.findIndex((c) => !c.done);
            return unit.classes.map((cls, i) => {
              const state: 'completed' | 'current' | 'future' =
                cls.done ? 'completed'
                : i === currentClassIdx ? 'current'
                : 'future';
              const rowEP = rowEPFor(i);

              const circleBackground =
                state === 'completed' ? `${textColor}22`
                : state === 'current' ? `${textColor}10`
                : 'transparent';
              const circleBorder =
                state === 'completed' ? `2px solid ${textColor}44`
                : state === 'current' ? `2px solid ${textColor}99`
                : `2px dashed ${textColor}20`;
              const labelOpacity =
                state === 'completed' ? 0.9
                : state === 'current' ? 0.85
                : 0.68;

              return (
                <div
                  key={cls.classId}
                  aria-current={state === 'current' ? 'step' : undefined}
                  aria-label={`${cls.name ?? ''} — ${state}`}
                  role="button"
                  tabIndex={0}
                  className="tactile-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '12px 14px',
                    marginBottom: i < unit.classes.length - 1 ? 8 : 0,
                    color: textColor,
                    animation: isExpanded
                      ? `fadeSlideIn 0.4s ease ${0.25 + i * 0.1}s both`
                      : 'none',
                    ['--tactile-row-bg' as string]: `${textColor}0E`,
                    ['--tactile-row-hover-bg' as string]: `${textColor}1A`,
                    ['--tactile-row-border' as string]: `${textColor}28`,
                    ['--tactile-row-shadow' as string]: `0 3px 0 ${textColor}26`,
                    ['--tactile-row-shadow-hover' as string]: `0 4px 0 ${textColor}38`,
                    ['--tactile-row-shadow-press' as string]: `0 1px 0 ${textColor}1A`,
                  } as React.CSSProperties}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({
                      to: '/classes/$classId/block/$blockIdx',
                      params: { classId: cls.classId, blockIdx: '0' },
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: '/classes/$classId/block/$blockIdx',
                        params: { classId: cls.classId, blockIdx: '0' },
                      });
                    }
                  }}
                >
                  {/* Tri-state completion circle */}
                  <div
                    className={state === 'current' ? 'class-tile-current-ring' : undefined}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: circleBackground,
                      border: circleBorder,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: state === 'completed' ? 13 : 11,
                      color: metaColor,
                      fontFamily: oswald,
                      fontWeight: 700,
                      flexShrink: 0,
                      opacity: state === 'future' ? 0.45 : 1,
                      transition: 'opacity 0.3s ease',
                      // currentColor trick: color is set via the parent span's color
                    } as React.CSSProperties}
                  >
                    {state === 'completed' ? '\u2713' : i + 1}
                  </div>
                  <span
                    style={{
                      fontFamily: dmSerif,
                      fontSize: 14,
                      color: metaColor,
                      opacity: labelOpacity,
                      fontWeight: state === 'current' ? 600 : 400,
                      fontStyle: state === 'future' ? 'italic' : 'normal',
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    {cls.name}
                  </span>
                  {state === 'completed' && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontFamily: oswald,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '0.18em',
                        color: metaColor,
                        opacity: showEP ? 0.55 : 0.7,
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {showEP
                        ? `✓ ${t('journey.epShort', { points: rowEP })}`
                        : t('journey.done')}
                    </span>
                  )}
                  {state === 'current' && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontFamily: oswald,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '0.18em',
                        color: metaColor,
                        opacity: 0.95,
                        padding: '2px 6px',
                        border: `1px solid ${metaColor}55`,
                        background: `${metaColor}08`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      NOW
                      {showEP
                        ? ` · ${t('journey.epShort', { points: rowEP })}`
                        : ''}
                    </span>
                  )}
                  {state === 'future' && showEP && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontFamily: oswald,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '0.18em',
                        color: metaColor,
                        opacity: 0.4,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('journey.epShort', { points: rowEP })}
                    </span>
                  )}
                </div>
              );
            });
          })()}

          {/* Badge + Action row */}
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: isMobile ? 12 : 0,
              marginTop: 20,
              animation: isExpanded
                ? `fadeSlideIn 0.4s ease ${0.25 + unit.classes.length * 0.1}s both`
                : 'none',
            }}
          >
            {isCompleted ? (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Selo
                  symbol={unit.woman?.symbol}
                  label={seloCharacteristic(unit.badge, unit.name)}
                  background={accent}
                  size={40}
                />
                <span
                  style={{
                    fontFamily: oswald,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    color: textColor,
                    opacity: 0.8,
                    textTransform: 'uppercase',
                  }}
                >
                  {seloCharacteristic(unit.badge, unit.name)}
                </span>
              </div>
            ) : (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 12px',
                  background: `${textColor}0C`,
                  border: `1px solid ${textColor}18`,
                }}
              >
                <span
                  style={{
                    fontFamily: oswald,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    color: textColor,
                    opacity: 0.7,
                  }}
                >
                  {unit.badge}
                </span>
              </div>
            )}

            {isCompleted && !unit.badgeClaimedAt && !isHistorical && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCeremony(unit);
                }}
                className="tactile-btn"
                style={{
                  padding: '10px 24px',
                  fontFamily: oswald,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  background: textColor,
                  color: bgColor === P.baby ? P.maxRed : bgColor,
                  border: 'none',
                  ['--tactile-shadow' as string]: `0 5px 0 ${textColor}66, 0 8px 16px rgba(0,0,0,0.18)`,
                  ['--tactile-shadow-hover' as string]: `0 6px 0 ${textColor}80, 0 10px 20px rgba(0,0,0,0.22)`,
                  ['--tactile-shadow-press' as string]: `0 1px 0 ${textColor}40, 0 2px 6px rgba(0,0,0,0.14)`,
                } as React.CSSProperties}
              >
                {t('journey.claimSelo')}
              </button>
            )}
            {isCurrent && !isHistorical && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNextClass();
                }}
                className="tactile-btn"
                style={{
                  padding: '10px 24px',
                  fontFamily: oswald,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  background: textColor,
                  color: bgColor === P.baby ? P.maxRed : bgColor,
                  border: 'none',
                  ['--tactile-shadow' as string]: `0 5px 0 ${textColor}55`,
                  ['--tactile-shadow-hover' as string]: `0 6px 0 ${textColor}66`,
                  ['--tactile-shadow-press' as string]: `0 1px 0 ${textColor}33`,
                } as React.CSSProperties}
              >
                {t('journey.continue')} &rarr;
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// -- WallSection: one unit with watermark number + title bar + poster --
function WallSection({
  unit,
  index,
  isExpanded,
  onToggle,
  onCeremony,
  isVisible,
  isMobile,
  journeyId,
  isHistorical,
  isNextLocked,
  ceremonyActive,
  onOpenStory,
  onAutoMeet,
}: {
  unit: WallUnit;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onCeremony: (unit: WallUnit) => void;
  isVisible: boolean;
  isMobile: boolean;
  journeyId: string;
  isHistorical: boolean;
  isNextLocked: boolean;
  ceremonyActive: boolean;
  onOpenStory?: (unitId: string) => void;
  onAutoMeet?: () => void;
}) {
  const isCompleted = unit.status === 'completed';
  const isCurrent = unit.status === 'in_progress';
  const active = isCompleted || isCurrent;
  const isEven = index % 2 === 0;
  const posterStyle = getPosterStyle(index);
  const bgColor = posterStyle.bg;
  const accent = bgColor === P.baby ? P.maxRed : bgColor;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMobile
          ? 'stretch'
          : isEven
            ? 'flex-start'
            : 'flex-end',
        padding: isMobile ? '0 14px' : '0 20px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${index * 0.1}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${index * 0.1}s`,
      }}
    >
      {/* Unit number -- large watermark */}
      <div
        style={{
          fontFamily: oswald,
          fontSize: isMobile ? 56 : 80,
          fontWeight: 700,
          color: active ? `${P.charm}18` : `${P.lightBorder}44`,
          lineHeight: 0.8,
          marginBottom: isMobile ? -14 : -20,
          marginLeft: isMobile ? -4 : isEven ? -8 : 'auto',
          marginRight: isMobile ? 'auto' : isEven ? 'auto' : -8,
          position: 'relative',
          zIndex: 0,
          userSelect: 'none',
        }}
      >
        {String(unit.order + 1).padStart(2, '0')}
      </div>

      {/* Unit title bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexDirection: isMobile ? 'row' : isEven ? 'row' : 'row-reverse',
          marginBottom: unit.description ? 10 : 14,
        }}
      >
        <div
          style={{
            fontFamily: oswald,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.18em',
            color: active ? accent : P.warmGray,
            padding: '3px 10px',
            background: active ? `${accent}0C` : 'transparent',
            border: `1px solid ${active ? accent + '25' : P.lightBorder}`,
            flexShrink: 0,
          }}
        >
          {isCompleted
            ? 'COMPLETED'
            : isCurrent
              ? 'IN PROGRESS'
              : 'LOCKED'}
        </div>
        <div
          style={{
            fontFamily: oswald,
            fontSize: isMobile ? 18 : 22,
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: active ? P.darkText : P.warmGray,
          }}
        >
          {unit.name}
        </div>
      </div>

      {/* Editorial standfirst (dek) — supporting unit description */}
      {unit.description && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            maxWidth: '60ch',
            marginBottom: 16,
            flexDirection: isMobile ? 'row' : isEven ? 'row' : 'row-reverse',
          }}
        >
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 24,
              height: 1,
              background: active ? accent : P.warmGray,
              opacity: 0.35,
              display: 'inline-block',
              marginTop: 10,
            }}
          />
          <div
            style={{
              fontFamily: dmSerif,
              fontSize: isMobile ? 14 : 15,
              color: P.midText,
              lineHeight: 1.65,
              textAlign: isMobile ? 'left' : isEven ? 'left' : 'right',
            }}
          >
            {unit.description}
          </div>
        </div>
      )}

      {/* Poster wrapper */}
      <div
        style={{
          width: isExpanded || isMobile ? '100%' : 'auto',
          maxWidth: isExpanded || isMobile ? '100%' : 380,
          display: 'flex',
          flexDirection: 'column',
          alignItems:
            isExpanded || isMobile
              ? 'stretch'
              : isEven
                ? 'flex-start'
                : 'flex-end',
          transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <ExpandablePoster
          unit={unit}
          index={index}
          isExpanded={isExpanded}
          onToggle={onToggle}
          onCeremony={onCeremony}
          isMobile={isMobile}
          journeyId={journeyId}
          isHistorical={isHistorical}
          isNextLocked={isNextLocked}
          ceremonyActive={ceremonyActive}
          onOpenStory={onOpenStory}
          onAutoMeet={onAutoMeet}
        />
      </div>
    </div>
  );
}

// -- StatusBlock: poster count with mini poster grid --
function StatusBlock({
  completedCount,
  totalUnits,
  units,
  isMobile,
}: {
  completedCount: number;
  totalUnits: number;
  units: WallUnit[];
  isMobile: boolean;
}) {
  const { t } = useTranslation('p2e');

  return (
    <div
      style={{
        margin: isMobile ? '0 14px 36px' : '0 24px 50px',
        padding: isMobile ? '22px 18px' : '28px 24px',
        background: P.white,
        border: `1px solid ${P.lightBorder}`,
        textAlign: 'center',
        boxShadow: `0 2px 16px ${P.shadow}`,
      }}
    >
      <div
        style={{
          fontFamily: oswald,
          fontSize: isMobile ? 28 : 36,
          fontWeight: 700,
          color: P.maxRed,
          letterSpacing: '0.04em',
          lineHeight: 1,
          marginBottom: 8,
        }}
      >
        {t(
          completedCount !== 1
            ? 'journey.statusUnitsCompleted_plural'
            : 'journey.statusUnitsCompleted',
          { count: completedCount },
        )}
      </div>

      {/* Sticker book: completed units are named mini-posters, the current
          unit is a progress ring, future units are visible empty frames —
          set-completion pull instead of anonymous rectangles. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 10,
          marginTop: 20,
          flexWrap: 'wrap',
        }}
      >
        {units.map((u, i) => {
          const isComp = u.status === 'completed';
          const isCurr = u.status === 'in_progress';
          const uStyle = getPosterStyle(i);
          const rot = ROTATIONS[i % ROTATIONS.length]! * 0.5;
          const initial =
            u.woman?.name?.charAt(0) ?? u.name?.charAt(0) ?? '?';
          const surname = u.woman?.name?.split(' ').pop() ?? u.name ?? '';
          const pct =
            (u.classesTotal ?? 0) > 0
              ? Math.round(((u.classesCompleted ?? 0) / u.classesTotal) * 100)
              : 0;
          return (
            <div key={u.unitId} style={{ width: 64, textAlign: 'center' }}>
              {isComp ? (
                <div
                  style={{
                    width: 52,
                    height: 68,
                    margin: '0 auto',
                    background: uStyle.bg,
                    color: uStyle.text,
                    position: 'relative',
                    transform: `rotate(${rot}deg)`,
                    boxShadow: `3px 4px 12px ${P.shadowDeep}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.5s ease',
                  }}
                >
                  <Tape
                    width={14}
                    left={4}
                    top={-3}
                    rotation={-12}
                    color={uStyle.tape}
                  />
                  <span
                    style={{ fontFamily: oswald, fontSize: 22, fontWeight: 700 }}
                  >
                    {initial}
                  </span>
                </div>
              ) : isCurr ? (
                <div
                  style={{
                    width: 52,
                    height: 68,
                    margin: '0 auto',
                    background: P.white,
                    border: `2px solid ${P.black}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: `conic-gradient(${P.black} ${pct}%, #EFE0DC 0)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: P.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: oswald,
                        fontSize: 9,
                        fontWeight: 700,
                        color: P.black,
                      }}
                    >
                      {u.classesCompleted ?? 0}/{u.classesTotal ?? 0}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: 52,
                    height: 68,
                    margin: '0 auto',
                    border: `2px dashed ${P.lightBorder}`,
                    background: `${P.roseMist}44`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: oswald,
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#D9C2BD',
                  }}
                >
                  ?
                </div>
              )}
              <div
                style={{
                  fontFamily: oswald,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: isComp ? P.midText : isCurr ? P.maxRed : P.warmGray,
                  opacity: isComp || isCurr ? 1 : 0.7,
                  marginTop: 6,
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {isComp
                  ? surname
                  : isCurr
                    ? t('journey.inProgressLabel')
                    : u.order + 1}
              </div>
            </div>
          );
        })}
      </div>

      {/* {totalUnits - completedCount > 0 && (
        <div
          style={{
            fontFamily: dmSerif,
            fontSize: 12,
            fontStyle: 'italic',
            color: P.warmGray,
            marginTop: 14,
          }}
        >
          {t('journey.emptyFrames', { count: totalUnits - completedCount })}
        </div>
      )} */}
    </div>
  );
}


// -- CelebrationOverlay: shown when a journey is completed. `subhead` says
//    what happens next (next journey ready / teacher sets it / path end). --
function CelebrationOverlay({
  subhead,
  onDismiss,
}: {
  subhead: string;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('p2e');
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        background: 'rgba(26, 16, 24, 0.88)',
        backdropFilter: 'blur(16px) saturate(0.8)',
        animation: 'fadeSlideIn 0.5s ease both',
      }}
    >
      {/* Confetti burst (reuse CEREMONY_STYLES keyframes) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 10,
          overflow: 'hidden',
        }}
      >
        {!prefersReducedMotion() && Array.from({ length: 28 }).map((_, i) => {
          const angle = (i / 28) * Math.PI * 2;
          const dist = 80 + (i % 3) * 60;
          const colors = [P.maxRed, P.charm, P.sizzle, P.baby, P.white];
          const color = colors[i % colors.length]!;
          const size = 5 + (i % 4);
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: size,
                height: size,
                background: color,
                borderRadius: i % 2 === 0 ? '50%' : 1,
                opacity: 0,
                animation: `confettiBurst 1.6s cubic-bezier(0.22,1,0.36,1) ${i * 0.03}s both`,
                ['--dx' as string]: `${Math.cos(angle) * dist}px`,
                ['--dy' as string]: `${Math.sin(angle) * dist - 50}px`,
                ['--rot' as string]: `${360 + i * 20}deg`,
              }}
            />
          );
        })}
      </div>

      {/* Headline */}
      <div
        style={{
          position: 'relative',
          zIndex: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          padding: '0 32px',
          textAlign: 'center',
          animation: 'slideReveal 0.6s cubic-bezier(0.22,1,0.36,1) 0.2s both',
        }}
      >
        <div
          style={{
            fontFamily: oswald,
            fontSize: 36,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: P.white,
            lineHeight: 1.1,
          }}
        >
          {t('journey.celebrate.headline')}
        </div>
        <div
          style={{
            fontFamily: dmSerif,
            fontSize: 16,
            fontStyle: 'italic',
            color: `${P.white}bb`,
            lineHeight: 1.5,
          }}
        >
          {subhead}
        </div>
        <button
          onClick={onDismiss}
          style={{
            marginTop: 24,
            fontFamily: oswald,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.2em',
            color: P.black,
            background: P.white,
            border: 'none',
            padding: '12px 36px',
            cursor: 'pointer',
          }}
        >
          {t('journey.celebrate.continue')}
        </button>
      </div>
    </div>
  );
}

// -- Main Page --
function JourneyPage() {
  const { t, i18n } = useTranslation('p2e');
  const navigate = useNavigate();
  const search = Route.useSearch();
  const celebrateJourneyId = search.celebrate;
  const [celebrationDismissed, setCelebrationDismissed] = useState(false);

  const [selectedJourneyId, setSelectedJourneyId] = useState<
    string | undefined
  >(search.journeyId);
  const [expandedUnitId, setExpandedUnitId] = useState<string | null>(null);
  const [ceremonyUnit, setCeremonyUnit] = useState<WallUnit | null>(null);
  const [ceremonyPhase, setCeremonyPhase] = useState<0 | 1 | 2>(0);
  // A journey's last class arrives with BOTH `?ceremony=` and `?celebrate=`:
  // the last unit's selo ceremony plays first, the celebration only once it
  // has closed (the ceremony param is stripped as soon as it opens).
  const showCelebration =
    !!celebrateJourneyId &&
    !celebrationDismissed &&
    !search.ceremony &&
    !ceremonyUnit;
  const [visibleUnits, setVisibleUnits] = useState<Set<number>>(new Set());
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const upNextRef = useRef<HTMLDivElement | null>(null);
  const [upNextVisible, setUpNextVisible] = useState(true);
  const [currentSectionVisible, setCurrentSectionVisible] = useState(false);

  const w = useWidth();
  const isMobile = w < 480;
  const isNarrow = w < 640;

  const { data, isLoading, isFetching, isPlaceholderData } = trpc.progress.getStudentJourneyWall.useQuery(
    { journeyId: selectedJourneyId },
    {
      // Switching journeys in the banner dropdown keeps the current wall on
      // screen until the other one arrives — otherwise the page collapses
      // to the loading skeleton and back, which reads as a blink.
      placeholderData: keepPreviousData,
    },
  );

  const utils = trpc.useUtils();
  const claimBadgeMutation = trpc.progress.claimBadge.useMutation({
    onSuccess: () => {
      utils.progress.getStudentJourneyWall.invalidate();
    },
  });

  const handleCeremonyClose = () => {
    if (ceremonyUnit && data?.journey) {
      claimBadgeMutation.mutate({
        journeyId: data.journey.journeyId,
        unitId: ceremonyUnit.unitId,
      });
    }
    setCeremonyUnit(null);
  };

  const toggle = useCallback((unitId: string) => {
    setExpandedUnitId((prev) => (prev === unitId ? null : unitId));
  }, []);

  // Arriving from a unit's last class (`?ceremony=<unitId>`): open the selo
  // ceremony automatically once the wall confirms the unit really is completed
  // and unclaimed. The ref makes this fire once — the param is stripped right
  // after so a refresh or a back navigation never replays the ceremony.
  // Decide only on SETTLED wall data: the copy cached before the class was
  // completed still shows the unit in progress, and completeClass has already
  // kicked off the refetch that will say "completed".
  const ceremonyRequestHandledRef = useRef(false);
  const wallSettled = isCeremonyWallSettled({ hasData: !!data, isFetching, isPlaceholderData });
  useEffect(() => {
    if (ceremonyRequestHandledRef.current) return;
    if (!search.ceremony || !data || !wallSettled) return;
    ceremonyRequestHandledRef.current = true;
    const unit = resolveCeremonyUnit(data.units, search.ceremony);
    if (unit) {
      setExpandedUnitId(unit.unitId);
      setCeremonyUnit(unit);
    }
    // Strip the request either way (an unresolvable one must not block the
    // celebration), keeping `celebrate` so it can follow the ceremony.
    navigate({
      to: '/journey',
      search: {
        celebrate: search.celebrate,
        journeyId: undefined,
        ceremony: undefined,
      },
      replace: true,
    });
  }, [data, wallSettled, search.ceremony, search.celebrate, navigate]);

  // Self-learning student whose path already has a next journey lined up
  // (`pathEnd.mode === 'auto-next'`): the server activated it while building
  // this wall, so move over and celebrate the one just finished. Never while
  // a ceremony or celebration is still pending on this wall. A journey she
  // already moved on from comes back as `advanced` and stays put — looking
  // back at an old journey must not replay the jump or the celebration.
  const autoNextHandledRef = useRef<string | null>(null);
  useEffect(() => {
    const pathEnd = data?.pathEnd;
    const finishedJourneyId = data?.journey?.journeyId;
    if (!pathEnd || pathEnd.mode !== 'auto-next' || !finishedJourneyId) return;
    if (search.ceremony || ceremonyUnit || search.celebrate) return;
    if (autoNextHandledRef.current === finishedJourneyId) return;
    autoNextHandledRef.current = finishedJourneyId;
    setSelectedJourneyId(pathEnd.nextJourneyId ?? undefined);
    navigate({
      to: '/journey',
      search: {
        journeyId: pathEnd.nextJourneyId ?? undefined,
        celebrate: finishedJourneyId,
        ceremony: undefined,
      },
      replace: true,
    });
  }, [data, search.ceremony, search.celebrate, ceremonyUnit, navigate]);

  // "Ela chegou": the story of the current unit's woman presents itself once
  // (banner, then the full-screen viewer) — only after the seal ceremony and
  // the celebration have closed, never on a read-only wall. Does nothing at
  // all while no unit has a published story.
  const storyFlow = useStoryFlow({
    journeyId: data?.journey?.journeyId,
    units: data?.units,
  });

  // "She's here": the poster of the unit that just unlocked sits at the top
  // of the wall, the woman big, until the student meets her (story), starts
  // the unit, or sends the poster down onto the wall. Once per unit
  // (arrivalSeenAt on the server, plus a session set while that saves).
  // Never over the seal ceremony, the celebration or a read-only wall.
  const screenBlocked = isStoryFlowBlocked({
    ceremonyParam: search.ceremony,
    ceremonyActive: !!ceremonyUnit,
    celebrationPending: !!celebrateJourneyId && !celebrationDismissed,
    autoNextPending: data?.pathEnd?.mode === 'auto-next',
    readOnly:
      (data?.journey?.isHistorical ?? false) ||
      (data?.access?.readOnly ?? false),
  });
  const [arrival, setArrival] = useState<{
    unitId: string;
    phase: 'shown' | 'sliding';
  } | null>(null);
  const [arrivalDismissed, setArrivalDismissed] = useState(
    () => new Set(sessionDismissedArrivals),
  );
  const markArrivalSeenMutation = trpc.progress.markArrivalSeen.useMutation({
    onSuccess: () => {
      utils.progress.getStudentJourneyWall.invalidate();
    },
  });
  // Right after a seal ceremony she is down at the unit she just finished:
  // the next unit is met in place, on its poster just below (see "Next
  // unit"), never announced at the top of the page, out of sight. The top
  // banner is for opening the wall fresh, when she is already at the top.
  const [afterCeremony, setAfterCeremony] = useState(false);
  useEffect(() => {
    if (ceremonyUnit) setAfterCeremony(true);
  }, [ceremonyUnit]);
  const arrivalCandidate = resolveArrivalUnit(data?.units, {
    wallSettled,
    blocked: screenBlocked || afterCeremony || !!ceremonyUnit,
    dismissed: arrivalDismissed,
  });
  const arrivalUnit = arrival
    ? data?.units.find((u) => u.unitId === arrival.unitId) ?? null
    : null;
  useEffect(() => {
    if (arrivalCandidate && !arrival) {
      setArrival({ unitId: arrivalCandidate.unitId, phase: 'shown' });
    } else if (arrival && !arrivalUnit && wallSettled) {
      // The wall switched journeys under the banner: drop it.
      setArrival(null);
    }
  }, [arrivalCandidate, arrival, arrivalUnit, wallSettled]);

  const journeyIdForArrival = data?.journey?.journeyId;
  const { mutate: markArrivalSeen } = markArrivalSeenMutation;
  const dismissArrival = useCallback(
    (unitId: string) => {
      sessionDismissedArrivals.add(unitId);
      setArrivalDismissed(new Set(sessionDismissedArrivals));
      if (journeyIdForArrival) {
        markArrivalSeen({ journeyId: journeyIdForArrival, unitId });
      }
    },
    [journeyIdForArrival, markArrivalSeen],
  );

  // The poster slides down onto its place on the wall: the banner collapses
  // while the unit's poster opens below; when the slide ends the wall scrolls
  // to that poster. Reduced motion: no slide, straight to the open poster.
  const slideTimerRef = useRef<number | null>(null);
  const unitsForArrival = data?.units;
  const settleArrival = useCallback(
    (unitId: string) => {
      dismissArrival(unitId);
      setExpandedUnitId(unitId);
      const reduced = prefersReducedMotion();
      const finish = () => {
        slideTimerRef.current = null;
        setArrival(null);
        const idx = unitsForArrival?.findIndex((u) => u.unitId === unitId) ?? -1;
        // One frame for the banner to leave the layout first.
        requestAnimationFrame(() => {
          const el = idx >= 0 ? sectionRefs.current[idx] : null;
          if (el) scrollSectionToTop(el, { reduced });
        });
      };
      if (reduced) {
        finish();
        return;
      }
      setArrival({ unitId, phase: 'sliding' });
      slideTimerRef.current = window.setTimeout(finish, ARRIVAL_SLIDE_MS);
    },
    [dismissArrival, unitsForArrival],
  );
  useEffect(
    () => () => {
      if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
    },
    [],
  );

  // "Next unit" from the seal ceremony: the next unit is met right where she
  // is — its poster (just below the one she finished) opens with the woman
  // big, the wall moves a short way down to it, and her story (when waiting)
  // opens by itself after 5 s. That counts as meeting the unit's arrival.
  const [inPlaceArrivalUnitId, setInPlaceArrivalUnitId] = useState<string | null>(null);
  const nextUnitScrollRef = useRef<{ raf: number; timer: number }>({ raf: 0, timer: 0 });
  const goToNextUnitInPlace = useCallback(
    (unitId: string) => {
      dismissArrival(unitId);
      setExpandedUnitId(unitId);
      setInPlaceArrivalUnitId(unitId);
      const idx = unitsForArrival?.findIndex((u) => u.unitId === unitId) ?? -1;
      if (idx < 0) return;
      const reduced = prefersReducedMotion();
      const handles = nextUnitScrollRef.current;
      cancelAnimationFrame(handles.raf);
      clearTimeout(handles.timer);
      let frames = 0;
      const go = () => {
        const el = sectionRefs.current[idx];
        if (!el) {
          if (frames++ < 60) handles.raf = requestAnimationFrame(go);
          return;
        }
        // A frame later, so the poster's open height is laid out.
        handles.raf = requestAnimationFrame(() => {
          scrollSectionToTop(el, { reduced });
          // Safety net: the poster above closes and this one opens while the
          // smooth scroll runs — land exactly on it.
          handles.timer = window.setTimeout(() => {
            const scroller = findScrollableAncestor(el);
            const viewTop = scroller ? scroller.getBoundingClientRect().top : 0;
            const offset = el.getBoundingClientRect().top - viewTop - SECTION_SCROLL_GAP;
            if (Math.abs(offset) > 24) scrollSectionToTop(el, { reduced: true });
          }, 900);
        });
      };
      handles.raf = requestAnimationFrame(go);
    },
    [dismissArrival, unitsForArrival],
  );
  useEffect(
    () => () => {
      cancelAnimationFrame(nextUnitScrollRef.current.raf);
      clearTimeout(nextUnitScrollRef.current.timer);
    },
    [],
  );

  // "Meet her": her story opens from the banner; when the viewer closes the
  // poster takes its place on the wall.
  const arrivalMeetingRef = useRef<{ unitId: string; opened: boolean } | null>(null);
  const storyActiveUnitId = storyFlow.activeUnitId;
  useEffect(() => {
    const meeting = arrivalMeetingRef.current;
    if (!meeting) return;
    if (storyActiveUnitId === meeting.unitId) {
      meeting.opened = true;
      return;
    }
    if (storyActiveUnitId === null && meeting.opened) {
      arrivalMeetingRef.current = null;
      settleArrival(meeting.unitId);
    }
  }, [storyActiveUnitId, settleArrival]);

  // Opening the wall: the current unit's poster is already open and in view,
  // so the next class is one tap away without scrolling. Once per journey;
  // not when a ceremony / celebration is about to play, and not while the
  // arrival banner leads (it settles the wall itself).
  const focusHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data?.journey || !wallSettled) return;
    if (search.ceremony || search.celebrate || screenBlocked || afterCeremony) return;
    const key = data.journey.journeyId;
    if (focusHandledRef.current === key) return;
    focusHandledRef.current = key;
    if (arrivalCandidate || arrival) return;
    const focus = findWallFocusUnit(data.units);
    if (!focus) return;
    setExpandedUnitId(focus.unitId);
    const idx = data.units.indexOf(focus);
    const reduced = prefersReducedMotion();
    let raf = 0;
    let frames = 0;
    const go = () => {
      const el = sectionRefs.current[idx];
      if (!el) {
        if (frames++ < 60) raf = requestAnimationFrame(go);
        return;
      }
      raf = requestAnimationFrame(() =>
        scrollSectionToTop(el, { reduced, onlyIfBelow: true }),
      );
    };
    raf = requestAnimationFrame(go);
    return () => cancelAnimationFrame(raf);
  }, [data, wallSettled, search.ceremony, search.celebrate, screenBlocked, afterCeremony, arrivalCandidate, arrival]);

  // In-place ceremony phases: 0 blackout → 1 stamp (~700ms) → 2 caption
  // (~1600ms). Reduced motion jumps straight to the caption. Also centers
  // the ceremony unit's poster in the viewport when the ceremony opens.
  useEffect(() => {
    if (!ceremonyUnit) return;
    const reduced = prefersReducedMotion();
    setCeremonyPhase(reduced ? 2 : 0);
    const idx =
      data?.units.findIndex((u) => u.unitId === ceremonyUnit.unitId) ?? -1;

    // Centering can't happen in this commit. When the ceremony opens on arrival
    // from a class, the units are rendering for the first time, so the section
    // ref is still null and a single immediate scrollIntoView silently no-ops —
    // the blackout comes up with the poster off-screen. Wait for the ref, then
    // scroll a frame later so the just-expanded card's height is laid out.
    let raf = 0;
    let frames = 0;
    if (idx >= 0) {
      const scrollToSection = () => {
        const el = sectionRefs.current[idx];
        if (!el) {
          if (frames++ < 60) raf = requestAnimationFrame(scrollToSection);
          return;
        }
        raf = requestAnimationFrame(() => {
          // Not scrollIntoView({block:'center'}): the caption plate owns the
          // bottom of the viewport, so a viewport-centred poster runs under it.
          // Centre in the band above the caption instead.
          const scroller = findScrollableAncestor(el);
          const rect = el.getBoundingClientRect();
          const viewTop = scroller ? scroller.getBoundingClientRect().top : 0;
          const viewHeight = scroller ? scroller.clientHeight : window.innerHeight;
          const available = Math.max(viewHeight - CEREMONY_CAPTION_BAND, 200);
          const delta = rect.top + rect.height / 2 - (viewTop + available / 2);
          const opts: ScrollToOptions = {
            top: delta,
            behavior: reduced ? 'auto' : 'smooth',
          };
          if (scroller) scroller.scrollBy(opts);
          else window.scrollBy(opts);
        });
      };
      raf = requestAnimationFrame(scrollToSection);
    }

    if (reduced) return () => cancelAnimationFrame(raf);
    const timers = [
      setTimeout(() => setCeremonyPhase(1), 700),
      setTimeout(() => setCeremonyPhase(2), 1600),
    ];
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ceremonyUnit]);

  // IntersectionObserver for section fade-in
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(
              (entry.target as HTMLElement).dataset.idx,
            );
            setVisibleUnits((prev) => new Set([...prev, idx]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [data]);

  // Sticky continue bar visibility: the bar shows only while NEITHER the
  // up-next card NOR the current unit's poster is on screen, so exactly one
  // continue affordance is visible at any scroll position.
  useEffect(() => {
    if (!data?.units) return;
    const idx = data.units.findIndex((u) => u.status === 'in_progress');
    if (idx < 0) return;
    const sectionEl = sectionRefs.current[idx];
    const upNextEl = upNextRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === sectionEl) {
            setCurrentSectionVisible(entry.isIntersecting);
          } else if (entry.target === upNextEl) {
            setUpNextVisible(entry.isIntersecting);
          }
        });
      },
      { threshold: 0.2 },
    );
    if (sectionEl) observer.observe(sectionEl);
    if (upNextEl) observer.observe(upNextEl);
    return () => observer.disconnect();
  }, [data]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!data?.journey) {
    return <NoJourneyState />;
  }

  const completedCount = data.units.filter(
    (u) => u.status === 'completed',
  ).length;
  const totalUnits = data.units.length;
  const totalEmp = data.progress?.totalEmpowermentPoints ?? 0;
  const progressPercent =
    totalUnits > 0 ? (completedCount / totalUnits) * 100 : 0;

  // One clear next step: the in-progress unit and its first unfinished class.
  const currentUnit = data.units.find((u) => u.status === 'in_progress');
  const currentClassIdx = currentUnit
    ? currentUnit.classes.findIndex((c) => !c.done)
    : -1;
  const currentClass =
    currentUnit && currentClassIdx >= 0
      ? currentUnit.classes[currentClassIdx]
      : undefined;
  const firstLockedIndex = data.units.findIndex((u) => u.locked);
  const isHistoricalJourney = data.journey.isHistorical ?? false;
  // Purchased/granted access window (server-computed). Read-only once the
  // window has closed: the wall and its classes still open, seals stay, but
  // there is no next step to offer and classes can't be finished.
  const access = data.access ?? null;
  const isAccessReadOnly = access?.readOnly ?? false;
  const accessEndsAt = access?.endsAt ? new Date(access.endsAt) : null;
  const dateFnsLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;
  const accessEndsAtLabel = accessEndsAt
    ? format(accessEndsAt, 'PP', { locale: dateFnsLocale })
    : null;
  const accessNotice = isAccessReadOnly
    ? accessEndsAtLabel
      ? t('journey.accessExpired', { date: accessEndsAtLabel })
      : t('journey.accessReadOnly')
    : accessEndsAtLabel && access?.source === 'purchase'
      ? t('journey.validUntil', { date: accessEndsAtLabel })
      : null;
  // Every unit done and this journey is still the student's current one:
  // the wall is an end state (no next step to offer) until the next journey
  // is set — by her teacher, or never (end of the learning path).
  const pathEnd = data.pathEnd ?? null;
  const pathEndLine =
    pathEnd?.mode === 'teacher'
      ? t('journey.pathEnd.teacher')
      : pathEnd?.mode === 'path-end'
        ? t('journey.pathEnd.pathEnd')
        : pathEnd?.mode === 'advanced'
          ? t('journey.pathEnd.advanced')
          : null;
  const showUpNext =
    !!currentUnit &&
    !!currentClass &&
    !isHistoricalJourney &&
    !isAccessReadOnly &&
    !pathEnd &&
    // The arrival banner is the one next step while it is up.
    !arrival;
  const showStickyBar = showUpNext && !upNextVisible && !currentSectionVisible;
  const classesLeftInUnit = currentUnit
    ? Math.max(0, (currentUnit.classesTotal ?? 0) - (currentUnit.classesCompleted ?? 0))
    : 0;

  // Portrait ceremony (medallion): only when the unit's woman has a photo.
  // Without one the selo stamps the poster corner, as before.
  const ceremonyIndex = ceremonyUnit
    ? data.units.findIndex((u) => u.unitId === ceremonyUnit.unitId)
    : -1;
  // The unit the ceremony opens the way to (none after a journey's last unit:
  // the celebration follows instead).
  // A late "claim her seal" on an old poster has no next step to offer.
  const ceremonyFollowing = ceremonyIndex >= 0 ? data.units[ceremonyIndex + 1] ?? null : null;
  const ceremonyNextUnit =
    ceremonyFollowing && !ceremonyFollowing.locked && ceremonyFollowing.status !== 'completed'
      ? ceremonyFollowing
      : null;
  const ceremonyPortraitUrl = ceremonyUnit?.woman?.portraitUrl ?? null;
  const ceremonyHasMedallion = !!ceremonyPortraitUrl;
  const ceremonyPosterBg = getPosterStyle(Math.max(ceremonyIndex, 0)).bg;
  const ceremonyAccent =
    ceremonyPosterBg === P.baby ? P.maxRed : ceremonyPosterBg;

  const goToCurrentClass = () => {
    if (!currentClass) return;
    navigate({
      to: '/classes/$classId/block/$blockIdx',
      params: { classId: currentClass.classId, blockIdx: '0' },
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: P.cream,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Inject shared keyframes */}
      <style dangerouslySetInnerHTML={{ __html: CEREMONY_STYLES }} />

      {/* Subtle background pattern */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.3,
          background: `radial-gradient(ellipse at 20% 0%, ${P.baby}44 0%, transparent 50%),
                       radial-gradient(ellipse at 80% 100%, ${P.charm}18 0%, transparent 50%)`,
        }}
      />

      {/* Main content container */}
      <div
        style={{
          maxWidth: 640,
          margin: '0 auto',
          position: 'relative',
          zIndex: 5,
        }}
      >
        {/* Hero / Manifesto */}
        <div
          style={{
            padding: isMobile ? '40px 18px 36px' : '60px 24px 50px',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {/* Level banner — doubles as the journey switcher when the student
              has more than one journey */}
          <JourneyBanner
            level={data.journey.level ?? ''}
            name={data.journey.name ?? ''}
            currentJourneyId={data.journey.journeyId}
            onJourneyChange={setSelectedJourneyId}
            isMobile={isMobile}
          />

          {/* Historical read-only banner */}
          {data?.journey?.isHistorical && (
            <div
              style={{
                marginTop: 12,
                padding: '8px 16px',
                background: `${P.baby}`,
                border: `1px solid ${P.charm}`,
                borderRadius: 6,
                fontFamily: oswald,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: P.warmGray,
                textAlign: 'center',
              }}
            >
              {t('journey.historicalReadOnly')}
            </div>
          )}

          {/* Access window: "expired · read-only" or "valid until" line */}
          {accessNotice && (
            <div
              data-testid="access-notice"
              style={{
                marginTop: 12,
                padding: isAccessReadOnly ? '8px 16px' : 0,
                background: isAccessReadOnly ? `${P.baby}` : 'transparent',
                border: isAccessReadOnly ? `1px solid ${P.charm}` : 'none',
                borderRadius: 6,
                fontFamily: oswald,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: P.warmGray,
                textAlign: 'center',
              }}
            >
              {accessNotice}
            </div>
          )}

          {/* "She's here": the unit that just unlocked, the woman big, before
              its poster slides down onto the wall */}
          {arrival && arrivalUnit && (
            <ArrivalBanner
              key={arrival.unitId}
              unit={arrivalUnit}
              phase={arrival.phase}
              isMobile={isMobile}
              isNarrow={isNarrow}
              busy={storyFlow.activeUnitId === arrival.unitId}
              onStart={() => {
                dismissArrival(arrivalUnit.unitId);
                const next =
                  arrivalUnit.classes.find((c) => !c.done) ?? arrivalUnit.classes[0];
                if (!next) {
                  settleArrival(arrivalUnit.unitId);
                  return;
                }
                navigate({
                  to: '/classes/$classId/block/$blockIdx',
                  params: { classId: next.classId, blockIdx: '0' },
                });
              }}
              onToWall={() => settleArrival(arrivalUnit.unitId)}
              onMeet={
                canOpenStory(arrivalUnit) && !arrivalUnit.story?.seenAt
                  ? () => {
                      arrivalMeetingRef.current = {
                        unitId: arrivalUnit.unitId,
                        opened: false,
                      };
                      storyFlow.openStory(arrivalUnit.unitId);
                    }
                  : undefined
              }
            />
          )}

          {/* Up-next card: names the exact class to take next, one tap away.
              (Duolingo path-redesign finding: one obvious next action.) */}
          {showUpNext && currentUnit && currentClass && (
            <div
              ref={upNextRef}
              style={{
                marginTop: 18,
                textAlign: 'left',
                background: P.white,
                border: `1px solid ${P.lightBorder}`,
                borderLeft: `4px solid ${P.maxRed}`,
                boxShadow: `0 2px 14px ${P.shadow}`,
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: oswald,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    color: P.maxRed,
                  }}
                >
                  {t('journey.upNextLabel', {
                    unit: currentUnit.order + 1,
                    current: currentClassIdx + 1,
                    total: currentUnit.classesTotal ?? 0,
                  })}
                </div>
                <div
                  style={{
                    fontFamily: oswald,
                    fontSize: 17,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    color: P.darkText,
                    marginTop: 3,
                  }}
                >
                  {currentClass.name}
                </div>
                {currentUnit.woman?.name && (
                  <div
                    style={{
                      fontFamily: dmSerif,
                      fontSize: 13,
                      fontStyle: 'italic',
                      color: P.midText,
                      marginTop: 1,
                    }}
                  >
                    {t('journey.withWoman', { name: currentUnit.woman.name })}
                  </div>
                )}
              </div>
              <button
                onClick={goToCurrentClass}
                className="tactile-btn"
                style={{
                  marginLeft: 'auto',
                  flexShrink: 0,
                  fontFamily: oswald,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  color: P.white,
                  background: P.maxRed,
                  border: 'none',
                  padding: '12px 18px',
                  ['--tactile-shadow' as string]: '0 5px 0 rgba(120, 8, 14, 0.95)',
                  ['--tactile-shadow-hover' as string]: '0 6px 0 rgba(120, 8, 14, 1)',
                  ['--tactile-shadow-press' as string]: '0 1px 0 rgba(120, 8, 14, 0.95)',
                } as React.CSSProperties}
              >
                {t('journey.go')} &rarr;
              </button>
            </div>
          )}
        </div>

        {/* Wall Progress */}
        <div
          style={{
            margin: isMobile ? '0 14px 36px' : '0 24px 50px',
            padding: '20px 22px',
            background: P.white,
            border: `1px solid ${P.lightBorder}`,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 2px 16px ${P.shadow}`,
          }}
        >
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}
            >
              <span
                style={{
                  fontFamily: oswald,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: P.warmGray,
                }}
                data-testid="wall-label"
              >
                YOUR WALL
              </span>
              <span
                style={{
                  fontFamily: oswald,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: P.maxRed,
                }}
              >
                {totalEmp} EMPOWERMENT PTS
              </span>
            </div>

            {/* Poster blocks */}
            <div
              style={{
                display: 'flex',
                gap: 6,
                marginBottom: 10,
              }}
            >
              {data.units.map((u, i) => {
                const uStyle = getPosterStyle(i);
                const uBg = uStyle.bg;
                const uAccent = uBg === P.baby ? P.maxRed : uBg;
                const isComp = u.status === 'completed';
                const isCurr = u.status === 'in_progress';
                const uTapeColor = uStyle.tape;
                const scrollToUnit = () => {
                  const el = sectionRefs.current[i];
                  if (el)
                    el.scrollIntoView({
                      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
                      block: 'center',
                    });
                };
                const pct =
                  isCurr && (u.classesTotal ?? 0) > 0
                    ? ((u.classesCompleted ?? 0) / u.classesTotal) * 100
                    : 0;
                return (
                  <div
                    key={u.unitId}
                    role="button"
                    tabIndex={0}
                    aria-label={`${t('journey.myJourney')} \u2014 ${u.name ?? ''} ${u.order + 1}`}
                    style={{
                      flex: 1,
                      height: 48,
                      position: 'relative',
                      background: isComp
                        ? uBg
                        : isCurr
                          ? P.white
                          : P.blush,
                      border:
                        isComp || isCurr
                          ? `1.5px solid ${uAccent}`
                          : `1.5px solid ${P.lightBorder}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.5s ease',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={scrollToUnit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        scrollToUnit();
                      }
                    }}
                  >
                    {isComp && (
                      <Tape
                        width={16}
                        left={2}
                        top={-3}
                        rotation={-15}
                        color={uTapeColor}
                      />
                    )}
                    {/* Goal-gradient: the current unit's block fills class by
                        class instead of staying binary until the unit closes */}
                    {isCurr && pct > 0 && (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${pct}%`,
                            background: `${uAccent}14`,
                            transition: 'width 0.5s ease',
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            bottom: 0,
                            height: 4,
                            width: `${pct}%`,
                            background: uAccent,
                            transition: 'width 0.5s ease',
                          }}
                        />
                      </>
                    )}
                    {isCurr && (u.classesTotal ?? 0) > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 5,
                          right: 5,
                          fontFamily: oswald,
                          fontSize: 8,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          color: uAccent,
                          zIndex: 2,
                        }}
                      >
                        {u.classesCompleted ?? 0}/{u.classesTotal}
                      </span>
                    )}
                    <span
                      style={{
                        fontFamily: oswald,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        color: isComp
                          ? uStyle.text
                          : isCurr
                            ? uAccent
                            : P.warmGray,
                        position: 'relative',
                        zIndex: 2,
                      }}
                    >
                      {isComp
                        ? `\u2713 ${u.order + 1}`
                        : isCurr
                          ? `\u25C9 ${u.order + 1}`
                          : `${u.order + 1}`}
                    </span>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontFamily: dmSerif,
                  fontSize: 13,
                  fontStyle: 'italic',
                  color: P.midText,
                }}
                data-testid="poster-count"
              >
                {t('journey.postersPastedCount', { count: completedCount })}
              </span>
              {/* Goal-gradient framing: count down to the NEXT poster, not the
                  far-away end of the journey */}
              <span
                style={{
                  fontFamily: oswald,
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color:
                    currentUnit || pathEnd || completedCount === totalUnits
                      ? P.maxRed
                      : P.warmGray,
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: 5,
                }}
              >
                {!pathEnd && currentUnit && (currentUnit.classesTotal ?? 0) > 0 ? (
                  <>
                    <span style={{ fontSize: 16 }}>{classesLeftInUnit}</span>
                    {t('journey.classesToNextPoster', {
                      count: classesLeftInUnit,
                    })}
                  </>
                ) : pathEnd || (completedCount === totalUnits && totalUnits > 0) ? (
                  t('journey.wallComplete')
                ) : (
                  `${totalUnits - completedCount} TO GO`
                )}
              </span>
            </div>
            {/* End of the road for this journey: say what comes next. */}
            {pathEndLine && (
              <div
                data-testid="path-end-line"
                style={{
                  marginTop: 12,
                  fontFamily: dmSerif,
                  fontSize: 14,
                  fontStyle: 'italic',
                  color: P.midText,
                  lineHeight: 1.5,
                }}
              >
                {pathEndLine}
              </div>
            )}
          </div>
        </div>

        {/* Unit Sections */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 40 : 56,
            padding: isMobile ? '16px 0 30px' : '20px 0 40px',
            position: 'relative',
          }}
        >
          {data.units.flatMap((unit, i) => {
            const prevCompleted =
              i > 0 && data.units[i - 1]!.status === 'completed';
            // Alternate tilt + color per gap so it reads as hand-applied tape.
            const tilt = i % 2 === 0 ? -8 : 7;
            const tapeColor = prevCompleted
              ? `${P.maxRed}30`
              : i % 3 === 0
                ? `${P.charm}38`
                : i % 3 === 1
                  ? `${P.baby}66`
                  : `${P.sizzle}28`;

            const isCeremonyUnit = ceremonyUnit?.unitId === unit.unitId;

            const section = (
              <div
                key={unit.unitId}
                ref={(el) => {
                  sectionRefs.current[i] = el;
                }}
                data-idx={i}
                style={
                  isCeremonyUnit
                    ? {
                        position: 'relative',
                        zIndex: 50,
                        // The medallion is the hero; the poster steps back.
                        filter: ceremonyHasMedallion
                          ? 'brightness(0.42)'
                          : undefined,
                        transition: 'filter 0.45s ease',
                      }
                    : undefined
                }
              >
                <WallSection
                  unit={unit}
                  index={i}
                  isExpanded={expandedUnitId === unit.unitId}
                  onToggle={() => toggle(unit.unitId)}
                  onCeremony={setCeremonyUnit}
                  isVisible={visibleUnits.has(i)}
                  isMobile={isMobile}
                  journeyId={data.journey.journeyId}
                  isHistorical={data.journey.isHistorical ?? false}
                  isNextLocked={i === firstLockedIndex}
                  ceremonyActive={
                    isCeremonyUnit && ceremonyPhase >= 1 && !ceremonyHasMedallion
                  }
                  onOpenStory={(unitId) => {
                    // Never over the seal ceremony.
                    if (!ceremonyUnit) storyFlow.openStory(unitId);
                  }}
                  onAutoMeet={
                    inPlaceArrivalUnitId === unit.unitId &&
                    expandedUnitId === unit.unitId &&
                    !ceremonyUnit &&
                    !storyFlow.activeUnitId &&
                    canOpenStory(unit) &&
                    !unit.story?.seenAt
                      ? () => {
                          setInPlaceArrivalUnitId(null);
                          storyFlow.openStory(unit.unitId);
                        }
                      : undefined
                  }
                />
              </div>
            );

            if (i === 0) return [section];

            const connector = (
              <div
                key={`tape-${unit.unitId}`}
                aria-hidden
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  // Negative margins pull the tape into the parent gap so it
                  // visually bridges sections instead of pushing them apart.
                  margin: isMobile ? '-20px 0' : '-28px 0',
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    width: isMobile ? 64 : 88,
                    height: isMobile ? 14 : 16,
                    background: tapeColor,
                    transform: `rotate(${tilt}deg)`,
                    boxShadow: `0 2px 8px ${P.shadow}, 0 1px 0 rgba(255,255,255,0.4) inset`,
                    backdropFilter: 'blur(2px)',
                  }}
                />
              </div>
            );

            return [connector, section];
          })}
        </div>

        {/* StatusBlock */}
        <StatusBlock
          completedCount={completedCount}
          totalUnits={totalUnits}
          units={data.units}
          isMobile={isMobile}
        />

        {/* Link to the full mural */}
        <div
          style={{
            textAlign: 'center',
            margin: isMobile ? '0 14px 44px' : '0 24px 60px',
          }}
        >
          <button
            onClick={() => navigate({ to: '/mural' })}
            className="tactile-btn"
            style={{
              background: 'transparent',
              border: `1px solid ${P.lightBorder}`,
              color: P.maxRed,
              fontFamily: oswald,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.16em',
              padding: '12px 22px',
              ['--tactile-shadow' as string]: `0 4px 0 ${P.lightBorder}`,
              ['--tactile-shadow-hover' as string]: `0 5px 0 ${P.lightBorder}`,
              ['--tactile-shadow-press' as string]: `0 1px 0 ${P.lightBorder}`,
            } as React.CSSProperties}
          >
            {t('journey.seeFullMural')}
          </button>
        </div>

        {/* Sticky continue bar: as the last child of the tall column it sticks
            to the viewport bottom mid-scroll. Shown only while neither the
            up-next card nor the current poster is on screen (see observer). */}
        {showUpNext && currentUnit && currentClass && (
          <div
            style={{
              position: 'sticky',
              bottom: 14,
              zIndex: 40,
              margin: isMobile ? '0 14px' : '0 24px',
              background: P.black,
              color: P.white,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              boxShadow: '0 10px 28px rgba(26, 16, 24, 0.4)',
              opacity: showStickyBar ? 1 : 0,
              transform: showStickyBar ? 'translateY(0)' : 'translateY(96px)',
              pointerEvents: showStickyBar ? 'auto' : 'none',
              transition: prefersReducedMotion()
                ? 'opacity 0.3s ease'
                : 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease',
            }}
            aria-hidden={!showStickyBar}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: oswald,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  color: P.charm,
                }}
              >
                {t('journey.upNextShort', { unit: currentUnit.order + 1 })}
              </div>
              <div
                style={{
                  fontFamily: oswald,
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  marginTop: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {currentClass.name}
              </div>
            </div>
            <button
              onClick={goToCurrentClass}
              tabIndex={showStickyBar ? 0 : -1}
              className="tactile-btn"
              style={{
                marginLeft: 'auto',
                flexShrink: 0,
                fontFamily: oswald,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.16em',
                color: P.white,
                background: P.maxRed,
                border: 'none',
                padding: '10px 16px',
                ['--tactile-shadow' as string]: '0 4px 0 rgba(120, 8, 14, 0.95)',
                ['--tactile-shadow-hover' as string]: '0 5px 0 rgba(120, 8, 14, 1)',
                ['--tactile-shadow-press' as string]: '0 1px 0 rgba(120, 8, 14, 0.95)',
              } as React.CSSProperties}
            >
              {t('journey.continue')}
            </button>
          </div>
        )}

        {/* In-place ceremony: blackout backdrop. Rendered inside this
            container so the ceremony unit's section (zIndex 50) can stack
            above it. Clicking anywhere closes — and claims the selo. */}
        {ceremonyUnit && (
          <div
            onClick={handleCeremonyClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 45,
              background: 'rgba(26,16,24,0.92)',
              backdropFilter: 'blur(6px)',
              animation: 'ceremonyFade 0.45s ease both',
              cursor: 'pointer',
            }}
          />
        )}

        {/* In-place ceremony: portrait medallion. Her photo reveals over the
            dimmed poster, centred in the band above the caption plate, and the
            selo slams onto its lower-right edge — the seal lands on her. */}
        {ceremonyUnit && ceremonyPortraitUrl && (
          <div
            style={{
              position: 'fixed',
              left: '50%',
              top: `calc((100% - ${CEREMONY_CAPTION_BAND}px) / 2)`,
              transform: 'translate(-50%, -50%)',
              zIndex: 55,
              pointerEvents: 'none',
            }}
          >
            <div
              className="ceremony-medallion"
              style={{
                position: 'relative',
                width: CEREMONY_MEDALLION,
                height: CEREMONY_MEDALLION,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: P.white,
                  padding: 5,
                  boxSizing: 'border-box',
                  boxShadow: `0 18px 48px rgba(0,0,0,0.55), 0 0 0 3px ${P.charm}`,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    position: 'relative',
                    background: P.offWhite,
                  }}
                >
                  <img
                    src={ceremonyPortraitUrl}
                    alt={ceremonyUnit.woman?.name ?? ''}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      display: 'block',
                    }}
                  />
                  {/* inner dashed ring, echoing the selo's ink-stamp ring */}
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 7,
                      borderRadius: '50%',
                      border: '1.2px dashed rgba(26,16,24,0.28)',
                    }}
                  />
                </div>
              </div>
              {ceremonyPhase >= 1 && (
                <div
                  style={{
                    position: 'absolute',
                    right: -16,
                    bottom: -4,
                    width: 84,
                    height: 84,
                    zIndex: 6,
                  }}
                >
                  <div
                    aria-hidden
                    className="selo-stamp-ring"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      border: `2px solid ${P.white}`,
                      opacity: 0,
                    }}
                  />
                  <div
                    className="selo-stamp"
                    style={{ transform: 'rotate(12deg)' }}
                  >
                    <Selo
                      symbol={ceremonyUnit.woman?.symbol}
                      label={seloCharacteristic(
                        ceremonyUnit.badge,
                        ceremonyUnit.name,
                      )}
                      background={ceremonyAccent}
                      size={84}
                      rotate={0}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* In-place ceremony: caption plate */}
        {ceremonyUnit && ceremonyPhase >= 2 && (
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              // Above the ceremony unit's section (zIndex 50) — otherwise a
              // tall expanded card centred over the bottom of the viewport
              // covers the caption, and the ceremony reads as half-played.
              zIndex: 60,
              textAlign: 'center',
              pointerEvents: 'none',
              padding: '48px 24px 28px',
              // Scrim keeps the caption legible on the frames where the poster
              // does overlap it.
              background:
                'linear-gradient(to top, rgba(26,16,24,0.96) 45%, rgba(26,16,24,0))',
              animation: 'fadeSlideIn 0.5s ease both',
            }}
          >
            <div
              style={{
                fontFamily: oswald,
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: '0.18em',
                color: P.white,
              }}
            >
              {t('journey.ceremonyPasted')}
            </div>
            <div
              style={{
                fontFamily: oswald,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.14em',
                color: P.charm,
                marginTop: 6,
              }}
            >
              {t('journey.ceremonySelo', {
                characteristic: seloCharacteristic(
                  ceremonyUnit.badge,
                  ceremonyUnit.name,
                ).toUpperCase(),
              })}
            </div>
            <div
              style={{
                fontFamily: dmSerif,
                fontSize: 12,
                fontStyle: 'italic',
                color: 'rgba(255,255,255,0.65)',
                marginTop: 4,
              }}
            >
              {t('journey.ceremonyFrom', {
                name: ceremonyUnit.woman?.name ?? ceremonyUnit.name,
              })}
            </div>
            {/* Two ways on: the next unit (closes the ceremony and opens the
                next unit's poster just below, in place — or just closes it
                after a journey's last unit), or visit the wall of posters. Both claim the seal — tapping anywhere else does the
                same and stays here. */}
            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginTop: 16,
                pointerEvents: 'auto',
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCeremonyClose();
                  if (ceremonyNextUnit) goToNextUnitInPlace(ceremonyNextUnit.unitId);
                }}
                className="tactile-btn"
                data-testid={ceremonyNextUnit ? 'ceremony-next-unit' : 'ceremony-continue'}
                style={{
                  fontFamily: oswald,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  color: P.white,
                  background: P.maxRed,
                  border: 'none',
                  padding: '11px 22px',
                  minHeight: 44,
                  ['--tactile-shadow' as string]: '0 5px 0 rgba(120, 8, 14, 0.95), 0 8px 16px rgba(0,0,0,0.35)',
                  ['--tactile-shadow-hover' as string]: '0 6px 0 rgba(120, 8, 14, 1), 0 10px 20px rgba(0,0,0,0.4)',
                  ['--tactile-shadow-press' as string]: '0 1px 0 rgba(120, 8, 14, 0.95), 0 2px 6px rgba(0,0,0,0.3)',
                } as React.CSSProperties}
              >
                {ceremonyNextUnit
                  ? t('journey.ceremonyNextUnit')
                  : t('journey.ceremonyContinue')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCeremonyClose();
                  navigate({ to: '/mural' });
                }}
                className="tactile-btn"
                data-testid="ceremony-see-wall"
                style={{
                  fontFamily: oswald,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  color: P.white,
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.55)',
                  padding: '11px 22px',
                  minHeight: 44,
                  ['--tactile-shadow' as string]: '0 5px 0 rgba(255, 255, 255, 0.18)',
                  ['--tactile-shadow-hover' as string]: '0 6px 0 rgba(255, 255, 255, 0.26)',
                  ['--tactile-shadow-press' as string]: '0 1px 0 rgba(255, 255, 255, 0.18)',
                } as React.CSSProperties}
              >
                {t('journey.seeYourMural')}
              </button>
            </div>
            <div
              style={{
                fontFamily: oswald,
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '0.25em',
                color: 'rgba(255,255,255,0.4)',
                marginTop: 12,
              }}
            >
              {ceremonyNextUnit ? t('journey.ceremonyTapStay') : t('journey.ceremonyTap')}
            </div>
          </div>
        )}

      </div>

      {/* Journey Completion Celebration */}
      {showCelebration && (
        <CelebrationOverlay
          // "Your next journey is ready" only when one actually exists;
          // otherwise say who sets the next one (or that the path is done).
          subhead={pathEndLine ?? t('journey.celebrate.subhead')}
          onDismiss={() => {
            setCelebrationDismissed(true);
            // Land on whichever journey is active now: the next one when the
            // path advanced, this same wall (with its end line) otherwise.
            setSelectedJourneyId(undefined);
            navigate({
              to: '/journey',
              search: { celebrate: undefined, journeyId: undefined, ceremony: undefined },
            });
          }}
        />
      )}

      {/* Her story, full-screen (portals to <body>) */}
      {storyFlow.viewer}
    </div>
  );
}

// -- Loading Skeleton: pasted-poster wall placeholder with shimmer sweep.
//    Follows NN/g guidance (skeleton > spinner for full-page loads) and
//    reuses the existing <Tape> component + shineSweep keyframe. --
function LoadingSkeleton() {
  const { t } = useTranslation('p2e');

  const placeholders = [
    { tilt: -2.2, tapeColor: `${P.maxRed}22`, bg: P.blush, delay: 0 },
    { tilt: 1.6, tapeColor: `${P.charm}30`, bg: P.roseMist, delay: 0.35 },
    { tilt: -1.1, tapeColor: `${P.sizzle}22`, bg: P.blush, delay: 0.7 },
  ];

  // Inject loader-specific keyframe (only one we don't already have).
  return (
    <div
      style={{
        minHeight: '100vh',
        background: P.cream,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 36,
        padding: '40px 20px',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: CEREMONY_STYLES }} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          alignItems: 'center',
        }}
      >
        {placeholders.map((p, i) => (
          <div
            key={i}
            aria-hidden
            style={{
              width: 260,
              height: 92,
              background: p.bg,
              position: 'relative',
              transform: `rotate(${p.tilt}deg)`,
              boxShadow: `4px 6px 18px ${P.shadow}`,
              overflow: 'hidden',
              border: `1px solid ${P.lightBorder}`,
            }}
          >
            <Tape width={36} left={14} top={-6} rotation={-10} color={p.tapeColor} />
            <Tape width={28} right={18} top={-4} rotation={6} color={p.tapeColor} />

            {/* Shimmer sweep: gradient strip translated across the poster. */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(105deg, transparent 30%, ${P.white}AA 50%, transparent 70%)`,
                animation: `shineSweep 1.8s ease ${p.delay}s infinite`,
                pointerEvents: 'none',
              }}
            />
          </div>
        ))}
      </div>

      <div
        role="status"
        aria-live="polite"
        style={{
          fontFamily: oswald,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.25em',
          color: P.warmGray,
          marginTop: 4,
        }}
      >
        {t('journey.loadingWall')}
      </div>
    </div>
  );
}

// -- No Journey State --
function NoJourneyState() {
  const { t } = useTranslation('p2e');

  return (
    <div
      style={{
        minHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        textAlign: 'center',
        background: P.cream,
      }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: P.baby,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 40,
        }}
      >
        <span role="img" aria-label="compass">
          &#x1F9ED;
        </span>
      </div>
      <h2
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: P.darkText,
          fontFamily: oswald,
        }}
      >
        {t('journey.noJourneyTitle')}
      </h2>
      <p
        style={{
          maxWidth: 400,
          fontSize: 14,
          color: P.midText,
        }}
      >
        {t('journey.noJourneyDescription')}
      </p>
    </div>
  );
}
